import { Injector } from '@angular/core';
import { FormActionTypeEnum, SEPAPaymentRailTypeEnum } from '@finxone-platform/form-action';
import {
  ActionButtonOutcomesType,
  BaseWidgetProperties,
  PaymentStatusScreen,
} from '@finxone-platform/shared/sys-config-types';
import { Actions, Store, ofActionDispatched } from '@ngxs/store';
import { map, take } from 'rxjs';
import { ClearFormDataActionWithId, ResetFormDataAction } from '../../../actions/form-submission.action';
import { CreatePayment, PaymentDeclined, PaymentSuccess } from '../../../actions/payment.action';
import { TransferMoneyRequest, paymentRailOptions } from '../../../services/account-service/account.type';
import { AccountState } from '../../../state/account.state';
import { FormActionState } from '../../../state/form-submision.state';
import { formatSchedulePaymentDate } from '../cta-actions/unified-payments/payment-utils';
import { handleRedirection } from '../cta-redirection.utils';

/**
 * Submits a domestic payment request using the provided injector to access
 * necessary services and state. It retrieves form data and the active account ID
 * from the store, dispatches a payment creation action, and listens for payment
 * success or decline actions. Upon receiving a payment status, it clears relevant
 * form data and redirects to the appropriate payment status screen.
 *
 * @param injector - The Angular injector used to access services like Store, Actions, and Router.
 */
export function submitDomesticPaymentRequest(
  injector: Injector,
  widgetProperties: BaseWidgetProperties,
): void {
  const store = injector.get(Store);
  const actions = injector.get(Actions);

  let formStateData = store.selectSnapshot(FormActionState.getFormActionState).response?.formData;
  const activeAccountId = store.selectSnapshot(AccountState.getCurrentAccountId);
  const sepaFormState = store.selectSnapshot(
    FormActionState.getFormActionStateWithId(FormActionTypeEnum.SEPA_PAYMENT),
  )?.formData;

  const schedulePaymentDate = formatSchedulePaymentDate(injector);

  formStateData = {
    ...formStateData,
    sourceAccountId: activeAccountId,
    ...(sepaFormState?.paymentRail && {
      scheme: paymentRailOptions[sepaFormState?.paymentRail as SEPAPaymentRailTypeEnum],
    }),
    schedulePaymentDate,
  };
  if (formStateData) {
    actions
      .pipe(
        ofActionDispatched(PaymentSuccess, PaymentDeclined),
        take(1),
        map((res) => {
          store.dispatch(new ClearFormDataActionWithId(FormActionTypeEnum.SEPA_PAYMENT));
          store.dispatch(new ClearFormDataActionWithId(FormActionTypeEnum.SEPA_PAYMENT_RAIL_FLAG));
          //set type based on the action dispatched after API call
          const type =
            res instanceof PaymentSuccess
              ? ActionButtonOutcomesType.SUCCESS
              : ActionButtonOutcomesType.FAILURE;
          //set navigation url based on the action dispatched after API call
          widgetProperties['urlToNavigate'] =
            res instanceof PaymentSuccess
              ? PaymentStatusScreen.PAYMENT_SUCCESS
              : PaymentStatusScreen.PAYMENT_DECLINED;
          handleRedirection(widgetProperties, injector, type);
        }),
      )
      .subscribe();
    store.dispatch(new CreatePayment(formStateData as TransferMoneyRequest));
  }
}
